import React from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { IconStarFilled, IconStarHalfFilled } from '@tabler/icons-react';
import type { EntryComponent } from 'cms/entries/entries';
import type { ICompareReviewQuote, ICompareReviews } from 'declarations/contentful';

const StarRating: React.FC<{ rating: number }> = ({ rating }) => {
  const fullStars = Math.floor(rating);
  const hasHalf = rating - fullStars >= 0.3;
  return (
    // eslint-disable-next-line jsx-a11y/prefer-tag-over-role -- composite of star images announced as a single rating
    <Row className="gap-px text-amber-600" role="img" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: fullStars }, (_, i) => (
        <IconStarFilled key={i} size={14} aria-hidden="true" />
      ))}
      {hasHalf && <IconStarHalfFilled size={14} aria-hidden="true" />}
    </Row>
  );
};

const ReviewCard: React.FC<{
  review: ICompareReviewQuote;
  variant: 'competitor' | 'opendoor';
}> = ({ review, variant }) => (
  <Col
    className={cn(
      'gap-3 rounded-xl border bg-od-bg-primary p-6',
      variant === 'opendoor' ? 'border-od-border-accent-brand' : 'border-od-border-secondary',
    )}
  >
    {variant === 'opendoor' && review.fields.starRating != null && (
      <StarRating rating={review.fields.starRating} />
    )}
    <Text
      as="p"
      className={cn(
        'text-body-sm leading-relaxed',
        variant === 'opendoor' ? 'text-od-content-primary' : 'text-od-content-secondary',
      )}
    >
      &ldquo;{review.fields.quote}&rdquo;
    </Text>
    {/* Atoms has no 11px (Bricks $bodyXsmall) type step; text-xs (12px) is the nearest scale token. */}
    <Text
      as="span"
      className={cn(
        'text-xs font-medium',
        variant === 'opendoor' ? 'text-od-content-accent-brand' : 'text-od-content-tertiary',
      )}
    >
      {variant === 'competitor' ? '— ' : ''}
      {review.fields.attribution}
    </Text>
  </Col>
);

const RenderCompareReviews = (entry: ICompareReviews) => {
  const { fields } = entry;

  return (
    <Col className="bg-od-bg-secondary py-20 max-md:py-12">
      <Col className="mx-auto w-full max-w-280 px-6">
        <Col className="gap-6">
          <Col className="gap-3">
            <Text as="p" className="text-label-sm font-medium text-od-content-accent-brand">
              {fields.eyebrow}
            </Text>
            <Text
              as="h2"
              className="text-subheader-lg text-od-content-primary max-md:text-subheader-md"
            >
              {fields.title}
            </Text>
            <Text as="p" className="max-w-145 text-body-lg text-od-content-secondary">
              {fields.description}
            </Text>
          </Col>

          <Col className="gap-12 md:flex-row">
            {/* Competitor column */}
            <Col className="flex-1 gap-4">
              <Row className="items-center gap-3">
                <Text as="span" className="text-label-md text-od-content-tertiary">
                  {fields.competitorName}
                </Text>
                <Row className="items-center gap-1">
                  <StarRating rating={fields.competitorRating} />
                  <Text as="span" className="text-xs text-od-content-tertiary">
                    {fields.competitorRating}
                    {fields.competitorReviewCount ? ` · ${fields.competitorReviewCount}` : ''}
                  </Text>
                </Row>
              </Row>
              <Col className="gap-4">
                {fields.competitorReviews.map((review) => (
                  <ReviewCard key={review.sys.id} review={review} variant="competitor" />
                ))}
              </Col>
            </Col>

            {/* Opendoor column */}
            <Col className="flex-1 gap-4">
              <Row className="items-center gap-3">
                <Text as="span" className="text-label-md text-od-content-accent-brand">
                  Opendoor
                </Text>
                <Row className="items-center gap-1">
                  <StarRating rating={fields.opendoorRating} />
                  <Text as="span" className="text-xs text-od-content-tertiary">
                    {fields.opendoorRating} · {fields.opendoorReviewCount}
                  </Text>
                </Row>
              </Row>
              <Col className="gap-4">
                {fields.opendoorReviews.map((review) => (
                  <ReviewCard key={review.sys.id} review={review} variant="opendoor" />
                ))}
              </Col>
            </Col>
          </Col>
        </Col>
      </Col>
    </Col>
  );
};

const CompareReviews: EntryComponent<ICompareReviews> = {
  render: RenderCompareReviews,
};

export default CompareReviews;
